// import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
// import { ReactComponent as LocationIcon } from "../../icons/locationIcon.svg";
import { capitalizeString, styleThousandCommas, getMembershipStatusColor, planRecurrenceMap, buildRequirementStatusDisplay, countEffectivelyMetRequirements, getMembershipCardKey } from '../../utils/helpers'
// import { usStates, USStatesEnum, ProfileTab } from "../../utils/enums"
// import { useCompleteSignIn, useUpdateAccountDetails, useAddCredential, useLoadAccountMemberships } from '../../hooks/authhooks'

// import UserAvatar from "../../components/UserAvatar";
import { CurrentRequirementsSchema, MembershipSchema, ExpandedMembershipSchema, AddRequirementStatus } from '../../data/types';
import { Error, KeyboardArrowRight, ReportProblem, CheckCircle } from '@mui/icons-material';

// // import { useGetPaymentMethods } from '../../hooks/paymenthooks'
// import { useGetMemberRequirementsStatus } from '../../hooks/authhooks'
// import { AddCredentialModal } from '../../molecules/AppModal';


// type AddRequirementStatus = { status: boolean, requirement?: CurrentRequirementsSchema }


type MembershipCardProps = {
  membership: MembershipSchema
  // isActive: boolean
  allMembershipCount: number
  expandedMemberships: {[key: string]: ExpandedMembershipSchema }
  /** Optional `?id=` query (community_id value) used as default expand key when it matches this membership */
  membershipQuery?: string | null
  toggleMembership: (communityId: string) => void
  toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
  handleSubmitNewMembership?: () => void
  handleSetViewCommunityResources: (communityId: string, isOpen: boolean) => void
  handleManageMembership: (membership: MembershipSchema) => void
}

const MembershipCard: React.FC<MembershipCardProps> = ({ membership, expandedMemberships, membershipQuery, handleSetViewCommunityResources, handleManageMembership, toggleMembership, toggleAddingCredential }) => {
  const membershipKey = getMembershipCardKey(membership, membershipQuery);
  const expandedMembership = expandedMemberships[membershipKey];
  const metRequirementsCount = expandedMembership?.requirementsStatus
    ? countEffectivelyMetRequirements(expandedMembership.requirementsStatus)
    : expandedMembership?.metRequirementsCount || 0;
  return (
    <div className="border-[1px]  border-[#E9E9E9] rounded-xl overflow-hidden">
      <div
        onClick={() => toggleMembership(membershipKey)}
        className={`flex flex-col md:flex-row md:items-center gap-5 md:gap-0 justify-between shadow-sm md:h-[170px] p-4 border-0 cursor-pointer hover:bg-gray-50 transition-colors ${expandedMembership?.isExpanded ? 'bg-[#FAFAFA] rounded-b-none' : 'bg-white'}`}
      >
        {/* Logo */}
        <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-white rounded-lg border mr-6">
          <img src={membership.logo} alt={membership.name} className="h-10 w-10 object-contain" />
        </div>
        {/* Info */}
        <div className="flex flex-col flex-1 min-w-0 gap-5 md:gap-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold font-raleway text-base text-black">{membership.name}</span>
            {
              membership.status && (
                <span 
                  className="font-raleway font-semibold text-xs ml-2 px-2 py-1 rounded-md"
                  style={{
                    backgroundColor: getMembershipStatusColor(membership.status || 'unknown').bgColor,
                    color: getMembershipStatusColor(membership.status || 'unknown').primaryTextColor
                  }}
                >
                  {capitalizeString(membership.status)}
                </span>
              )
            }
          </div>
          <span className="text-sm text-[#4D4D4D] mt-1 line-clamp-2 w-11/12">{(!membership?.description || membership?.description?.length < 300) ? membership.description : `${membership.description.slice(0, 300)}...`}</span>
          <div className="flex flex-row gap-2 mt-2">
            <span className="bg-white md:h-[35px] font-raleway items-center flex border-[1px] border-[#E9E9E9] text-[#4D4D4D] px-3 py-1 rounded-md text-xs font-medium">
              {membership.plan_name} Plan (${styleThousandCommas(membership?.plan_rate || 0)} {membership.plan_recurrence && `/ ${planRecurrenceMap[membership.plan_recurrence as keyof typeof planRecurrenceMap]}`})
            </span>
            <span className="bg-white hover:bg-gray-50 md:h-[35px] cursor-pointer font-raleway items-center flex border-[1px] border-[#E9E9E9] text-[#4D4D4D] px-3 py-1 rounded-md text-xs font-medium">
              {metRequirementsCount}/{expandedMembership?.totalRequirementsCount || 0} Requirements Met
              {expandedMembership?.isExpanded ? (
                <KeyboardArrowUpIcon className="text-[#A1A1A1] ml-1 my-auto" />
              ) : (
                <KeyboardArrowDownIcon className="text-[#A1A1A1] ml-1 my-auto" />
              )}
            </span>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex flex-col items-end gap-2" onClick={(e) => e.stopPropagation()}>
          {membership.status && (
            <button
              className="font-raleway bg-white border-[1px] border-primaryDark-0/30 text-primaryDark-0 rounded-md px-4 py-2 text-sm place-self-center font-semibold hover:bg-gray-50 hover:text-primaryDark-300 hover:border-primaryDark-300/30"
              onClick={() => handleManageMembership(membership)}
            >
              Manage Membership
            </button>
          )}
          <button
            className="font-raleway border-0 bg-primaryDark-0 text-white rounded-md px-4 py-2 text-sm place-self-center font-semibold hover:bg-primaryDark-300"
            onClick={() => handleSetViewCommunityResources(membership?.community_id || '', true)}
          >
            View Resources
            <KeyboardArrowRight className="text-[#A1A1A1]/70 ml-1 my-auto" />
          </button>
        </div>
      </div>

      {(expandedMembership?.isExpanded) && (
        <div className="p-4 border-t border-[#E9E9E9]">
          <RequiredCredentials 
            membershipRequirementsStatus={expandedMembership?.requirementsStatus} 
            metRequirementsCount={expandedMembership?.metRequirementsCount} 
            totalRequirementsCount={expandedMembership?.totalRequirementsCount} 
            toggleAddingCredential={toggleAddingCredential} 
          />
        </div>
      )}
    </div>
  )
}


type RequiredCredentialsProps = {
  membershipRequirementsStatus: CurrentRequirementsSchema[]
  metRequirementsCount: number
  totalRequirementsCount: number
  toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
}

const RequiredCredentials: React.FC<RequiredCredentialsProps> = ({ membershipRequirementsStatus, metRequirementsCount, totalRequirementsCount, toggleAddingCredential }) => {
  const computedMetRequirementsCount = membershipRequirementsStatus
    ? countEffectivelyMetRequirements(membershipRequirementsStatus)
    : metRequirementsCount;

  return (
    <>
      <div className="flex flex-row justify-between items-center w-full">
        <p className="text-[14px] font-open-sans text-[#808080] mb-4 mr-auto my-auto">
          {(membershipRequirementsStatus) ? `Complete your membership by adding required credentials.` : `No credentials required for this membership.`}
        </p>
        {(totalRequirementsCount > 0) && (
        <div className="flex items-center mb-4 ml-auto my-auto">
          <div className="flex flex-col w-full">
            <p className="text-[14px] font-raleway text-black text-right font-medium m-1">Progress Completion</p>
            <div className="relative w-[140px] h-2.5 rounded-md">
              <div className="absolute top-0 left-0 h-2.5 w-full bg-gray-300 rounded-md"></div>
              <div
              className="absolute top-0 left-0 h-2.5 bg-primaryDark-300 rounded-md"
              style={{ width: `${(computedMetRequirementsCount / totalRequirementsCount) * 100}%` }}
              ></div>
            </div>
          </div>
          <p className="text-sm pl-2 font-raleway font-medium">{totalRequirementsCount ? Math.round((computedMetRequirementsCount / totalRequirementsCount) * 100) : 0}%</p>
        </div>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {membershipRequirementsStatus && membershipRequirementsStatus.map((requirement, i) => {
          if (!requirement.is_required) return null;

          const statusDisplay = buildRequirementStatusDisplay(requirement);
          const StatusIcon =
            statusDisplay.statusTone === 'success'
              ? CheckCircle
              : statusDisplay.statusTone === 'warning'
                ? ReportProblem
                : statusDisplay.statusTone === 'error'
                  ? Error
                  : null;
          const statusIconColor =
            statusDisplay.statusTone === 'success'
              ? 'success'
              : statusDisplay.statusTone === 'warning'
                ? 'warning'
                : 'error';

          return (
            <div key={i} className={`flex flex-row items-start border-[1px] rounded-xl p-4 transition-colors ${statusDisplay.rowSurfaceClass} ${statusDisplay.rowHoverClass}`}>
              <div className="flex flex-row my-auto mx-0 w-full min-w-0">
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 items-center flex-1 min-w-0">
                  {statusDisplay.isEffectivelyMet ? (
                    <CheckBox className="shrink-0 text-primaryDark-300" />
                  ) : (
                    <CheckBoxOutlineBlankIcon className="shrink-0 text-[#CFCFCF]" />
                  )}
                  <div className="flex flex-row items-center flex-wrap gap-x-2 gap-y-1 min-w-0">
                    <p className="text-[14px] font-raleway text-black font-semibold">{requirement.name}</p>
                    {statusDisplay.statusLabel && StatusIcon && (
                      <div className="flex flex-row items-center shrink-0 ml-3">
                        <p className={`text-[12px] font-open-sans font-medium ${statusDisplay.statusTextClass}`}>
                          {statusDisplay.statusLabel}
                        </p>
                        <StatusIcon sx={{ fontSize: 14 }} color={statusIconColor as 'success' | 'warning' | 'error'} className="ml-1" />
                      </div>
                    )}
                  </div>
                  {(requirement.description) && (<p className="col-start-2 text-[12px] font-open-sans text-[#4D4D4D] mt-1">
                    {requirement.description}
                  </p>)}
                  {(statusDisplay.detailLabel && (!statusDisplay.isEffectivelyMet || statusDisplay.expirationInfo?.status === 'expiring_soon')) && (
                    <p className={`col-start-2 text-[12px] font-open-sans mt-1 ${statusDisplay.subtitleTextClass}`}>
                      {statusDisplay.detailLabel}
                    </p>
                  )}
                </div>
              </div>
              <RequirementButton requirement={requirement} toggleAddingCredential={toggleAddingCredential} />
            </div>
          );
        })}
      </div>
    </>
  )
}


type RequirementButtonProps = {
  requirement: CurrentRequirementsSchema
  toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
}

const RequirementButton: React.FC<RequirementButtonProps> = ({ requirement, toggleAddingCredential }) => {
  const navigate = useNavigate()
  return (
    <div className='flex flex-row w-full'>
      {
        (requirement.code === "PAY") ? (
          <button onClick={() => navigate('/wallet')} className='bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-[14px] rounded-md border-0 ml-auto h-[43px] my-auto mx-2'>Go to Payment Method</button>
        ) : (
            <div className='ml-auto my-auto'>
              {/* {
                (requirement?.is_required && requirement?.presigned_url) && (<button className='bg-white hover:bg-gray-300 border-[#E9E9E9] border-[1px] text-[#D42620] font-raleway font-bold text-[14px] rounded-md h-[43px] my-auto mx-2'>Remove Credential</button>)
              } */}
              <button onClick={() => toggleAddingCredential({ status: true, requirement })} className='bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-[14px] rounded-md border-0 ml-auto h-[43px] my-auto mx-2'>
                {(requirement?.is_required && requirement?.presigned_url) ? 'Change' : 'Add'}
              </button>
            </div>
        )
      }
    </div>
  )
}

export default MembershipCard